import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./DynamicForm.css";

const DynamicForm = ({ fields, apiUrl, successRedirect }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Handle input changes for flat fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle input changes for object subfields
  const handleSubFieldChange = (fieldName, subField, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: {
        ...(prev[fieldName] || {}),
        [subField]: value,
      },
    }));
  };

  // Handle input changes for array subfields
  const handleArrayChange = (fieldName, index, subField, value) => {
    setFormData((prev) => {
      const updatedArray = [...(prev[fieldName] || [])];
      if (!updatedArray[index]) {
        updatedArray[index] = {};
      }
      updatedArray[index][subField] = value;
      return {
        ...prev,
        [fieldName]: updatedArray,
      };
    });
  };

  // Handle adding a new item to an array field
  const handleAddItem = (fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), {}],
    }));
  };

  // Handle removing an item from an array field
  const handleRemoveItem = (fieldName, index) => {
    setFormData((prev) => {
      const updatedArray = [...(prev[fieldName] || [])];
      updatedArray.splice(index, 1);
      return {
        ...prev,
        [fieldName]: updatedArray,
      };
    });
  };

  // Submit handler wrapped in useCallback (fixes dependency warning)
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);

      try {
        console.log(formData); // Debug log
        await axios.post(apiUrl, formData, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        alert("Project created successfully!");
        navigate(successRedirect);
      } catch (error) {
        console.error("Error creating project:", error);
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, formData, navigate, successRedirect]
  );

  // Hotkey for form submission (Ctrl + S)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "s") {
        e.preventDefault();
        handleSubmit(e);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit]);

  return (
    <div className="dynamic-form">
      <h2>Create New Entry</h2>
      <form onSubmit={handleSubmit}>
        {fields.map((fieldObj, index) => (
          <div key={index} className="form-group">
            <label>{fieldObj.fields}</label>

            {/* Object subfields */}
            {fieldObj.type === "object" && fieldObj.subfields ? (
              <div className="object-group">
                {fieldObj.subfields.map((subField, subIndex) => (
                  <div key={subIndex} className="form-group">
                    <label>{subField.fields}</label>
                    <input
                      type="text"
                      name={`${fieldObj.fields}-${subField.fields}`}
                      value={formData[fieldObj.fields]?.[subField.fields] || ""}
                      onChange={(e) =>
                        handleSubFieldChange(
                          fieldObj.fields,
                          subField.fields,
                          e.target.value
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            ) : fieldObj.subfields ? (
              // Array subfields
              <div className="array-wrapper">
                {formData[fieldObj.fields]?.map((item, itemIndex) => (
                  <div key={itemIndex} className="array-group">
                    {fieldObj.subfields.map((subField, subIndex) => (
                      <div key={subIndex} className="form-group">
                        <label>{subField.fields}</label>
                        <input
                          type="text"
                          name={`${fieldObj.fields}-${subField.fields}`}
                          value={item[subField.fields] || ""}
                          onChange={(e) =>
                            handleArrayChange(
                              fieldObj.fields,
                              itemIndex,
                              subField.fields,
                              e.target.value
                            )
                          }
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveItem(fieldObj.fields, itemIndex)
                      }
                      className="remove-btn"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleAddItem(fieldObj.fields)}
                  className="add-btn"
                >
                  Add {fieldObj.fields}
                </button>
              </div>
            ) : (
              // Simple flat fields
              <input
                type="text"
                name={fieldObj.fields}
                value={formData[fieldObj.fields] || ""}
                onChange={handleInputChange}
                required
              />
            )}
          </div>
        ))}

        <div className="buttoneditPage">
          <button type="submit" disabled={loading}>
            {loading ? "Creating..." : "Create Project"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DynamicForm;
